import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useOutsideClick
} from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import usePromise from '@hooks/usePromise';
import api from '@api/endpoints';
import { formatNumberWithCommas } from '@utils';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Search and select...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false)
  });

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleSelect = (option) => {
    onChange(option);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <Box position="relative" ref={containerRef}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <MdSearch color="gray.300" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={isOpen ? searchTerm : value || ''}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          border="1px solid"
          borderColor="whiteAlpha.500"
          borderRadius="xl"
          background="blackAlpha.600"
          pl={8}
        />
      </InputGroup>
      {value && !isOpen && (
        <Button
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          size="xs"
          variant="ghost"
          onClick={handleClear}
          color="whiteAlpha.600"
          _hover={{ color: 'white' }}
          p={1}
          minW="auto"
          h="auto"
        >
          x
        </Button>
      )}
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="gray.800"
          border="1px solid"
          borderColor="whiteAlpha.300"
          borderRadius="lg"
          maxH="200px"
          overflowY="auto"
          zIndex={10}
        >
          <List spacing={0}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <ListItem
                  key={option}
                  px={3}
                  py={2}
                  cursor="pointer"
                  fontSize="sm"
                  bg={option === value ? 'whiteAlpha.200' : 'transparent'}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </ListItem>
              ))
            ) : (
              <ListItem px={3} py={2} fontSize="sm" color="whiteAlpha.500">
                No options found
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

const ManageClientReservesModal = ({
  clientName,
  subClientName,
  onClose,
  fetchDetails
}) => {
  const dispatch = useDispatch();
  const configs = useSelector((state) => state.falconDashboard.config);
  const payoutPartners = configs?.data?.payoutPartners || [];

  const [reserves, setReserves] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [amount, setAmount] = useState('');
  const [offset, setOffset] = useState('');
  const [operationType, setOperationType] = useState('credit');

  const { isLoading: isLoadingReserves, error: reservesError, data: reservesData, run: runGetReserves } = usePromise();
  const { isLoading: isSubmitting, run: runSubmit } = usePromise();

  const currencies = configs?.data?.currencies?.payout || [];

  const fetchReserves = useCallback(() => {
    const resPromise = api.falconDashboard.getClientReserves({
      clientName,
      subClientName
    });
    return runGetReserves(resPromise);
  }, [clientName, subClientName, runGetReserves]);

  useEffect(() => {
    fetchReserves();
  }, [fetchReserves]);

  useEffect(() => {
    if (reservesData?.data) {
      setReserves(reservesData.data);
    }
  }, [reservesData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVendor || !selectedCurrency || !amount) {
      dispatch(setAlert({
        title: 'Please fill in all required fields',
        status: ALERT_STATUS_TYPE.ERROR
      }));
      return;
    }

    const payload = {
      client_name: clientName,
      sub_client_name: subClientName,
      amount: parseFloat(amount),
      currency: selectedCurrency
    };

    const offsetTrimmed = offset.trim();
    if (offsetTrimmed !== '') {
      const parsedOffset = parseFloat(offsetTrimmed);
      if (Number.isNaN(parsedOffset)) {
        dispatch(setAlert({
          title: 'Please enter a valid offset or leave it empty',
          status: ALERT_STATUS_TYPE.ERROR
        }));
        return;
      }
      payload.offset = parsedOffset;
    }

    try {
      let resPromise;
      if (operationType === 'credit') {
        resPromise = api.falconDashboard.creditClientReserve({
          vendorName: selectedVendor,
          payload
        });
      } else {
        resPromise = api.falconDashboard.depleteClientReserve({
          vendorName: selectedVendor,
          payload
        });
      }

      await runSubmit(resPromise);

      dispatch(setAlert({
        title: `Reserve ${operationType === 'credit' ? 'credited' : 'depleted'} successfully`,
        status: ALERT_STATUS_TYPE.SUCCESS
      }));

      setAmount('');
      setOffset('');
      await fetchReserves();
      if (fetchDetails) {
        fetchDetails();
      }
    } catch (error) {
      dispatch(setAlert({
        title: error?.message || `Failed to ${operationType} reserve`,
        status: ALERT_STATUS_TYPE.ERROR
      }));
    }
  };

  return (
    <>
      <ModalHeader textAlign="center" py={0}>
        Manage Client Reserves
      </ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        <VStack spacing={4} align="stretch">
          <Box
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="xl"
            p={4}
            bg="blackAlpha.600"
          >
            <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
              Client: <Text as="span" color="white" fontWeight="bold">{clientName}</Text>
            </Text>
            {subClientName && (
              <Text fontSize="sm" color="whiteAlpha.700">
                Sub Client: <Text as="span" color="white" fontWeight="bold">{subClientName}</Text>
              </Text>
            )}
          </Box>

          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={2} color="brand.500">
              Existing Reserves
            </Text>
            {isLoadingReserves ? (
              <Flex justifyContent="center" alignItems="center" py={4}>
                <Spinner thickness="2px" size="md" color="colorPrimary" />
              </Flex>
            ) : reservesError ? (
              <Box color="red.500" p={4} borderRadius="md" borderWidth="1px">
                <Text fontSize="sm">Error loading reserves: {reservesError}</Text>
              </Box>
            ) : reserves && reserves.length > 0 ? (
              <Box
                border="1px solid"
                borderColor="whiteAlpha.500"
                borderRadius="lg"
                overflow="hidden"
              >
                <Table size="sm" variant="unstyled">
                  <Thead borderBottom="1px solid" borderColor="whiteAlpha.500">
                    <Tr>
                      <Th fontSize="xs" fontWeight="semibold">Vendor</Th>
                      <Th fontSize="xs" fontWeight="semibold">Currency</Th>
                      <Th fontSize="xs" fontWeight="semibold" isNumeric>Reserve</Th>
                      <Th fontSize="xs" fontWeight="semibold" isNumeric>Offset</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {reserves.map((reserve, index) => (
                      <Tr key={index}>
                        <Td fontSize="sm">{reserve.vendor_name || reserve.vendorName}</Td>
                        <Td fontSize="sm">{reserve.currency}</Td>
                        <Td fontSize="sm" isNumeric>
                          {formatNumberWithCommas(reserve.reserve)}
                        </Td>
                        <Td fontSize="sm" isNumeric>
                          {formatNumberWithCommas(reserve.offset || 0)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text fontSize="sm" color="whiteAlpha.600">
                No reserves found for this client
              </Text>
            )}
          </Box>

          <Box
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="xl"
            p={4}
            bg="blackAlpha.600"
          >
            <Text fontSize="md" fontWeight="semibold" mb={4} color="brand.500">
              {operationType === 'credit' ? 'Credit Reserve' : 'Deplete Reserve'}
            </Text>

            <FormControl as="form" onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color="whiteAlpha.700">
                      Operation Type
                    </FormLabel>
                    <Select
                      size="sm"
                      border="1px solid"
                      borderColor="whiteAlpha.500"
                      borderRadius="xl"
                      background="blackAlpha.600"
                      value={operationType}
                      onChange={(e) => setOperationType(e.target.value)}
                    >
                      <option value="credit">Credit (Add)</option>
                      <option value="deplete">Deplete (Remove)</option>
                    </Select>
                  </FormControl>
                </HStack>

                <HStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color="whiteAlpha.700">
                      Vendor
                    </FormLabel>
                    <SearchableSelect
                      options={payoutPartners}
                      value={selectedVendor}
                      onChange={setSelectedVendor}
                      placeholder="Search vendor..."
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color="whiteAlpha.700">
                      Currency
                    </FormLabel>
                    <Select
                      size="sm"
                      border="1px solid"
                      borderColor="whiteAlpha.500"
                      borderRadius="xl"
                      background="blackAlpha.600"
                      placeholder="Select currency"
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                    >
                      {currencies.map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" color="whiteAlpha.700">
                    Amount
                  </FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius="xl"
                    background="blackAlpha.600"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="whiteAlpha.700">
                    Offset <Text as="span" fontWeight="normal" color="whiteAlpha.500">(optional)</Text>
                  </FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    step="any"
                    border="1px solid"
                    borderColor="whiteAlpha.500"
                    borderRadius="xl"
                    background="blackAlpha.600"
                    placeholder="Optional"
                    value={offset}
                    onChange={(e) => setOffset(e.target.value)}
                  />
                </FormControl>

                <ModalFooter justifyContent="center" gap={4} px={0}>
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    colorScheme="white"
                    bg="whiteAlpha.300"
                    width="100%"
                    size="md"
                    fontSize="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    colorScheme={operationType === 'credit' ? 'green' : 'red'}
                    width="100%"
                    size="md"
                    fontSize="sm"
                    isLoading={isSubmitting}
                  >
                    {operationType === 'credit' ? 'Credit Reserve' : 'Deplete Reserve'}
                  </Button>
                </ModalFooter>
              </VStack>
            </FormControl>
          </Box>
        </VStack>
      </ModalBody>
    </>
  );
};

export default ManageClientReservesModal;
